import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';

export interface ReviewCaseItem {
  id: string;
  businessProfileName: string;
  status: string;
  policyCategory: string;
  reviewAuthor: string | null;
  reviewRating: number | null;
  clientAuthorizationConfirmed: boolean;
  clientApprovedAt: string | null;
  submittedAt: string | null;
  createdAt: string;
  companyName: string | null;
  contactEmail: string | null;
}

export const reviewCasesQuery = {
  queryKey: ['review-cases'],
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    api.get<{ items: ReviewCaseItem[]; complianceNotice: string }>('/api/review-cases', signal),
};

export function ReviewCasesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(reviewCasesQuery);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  const items = data?.items ?? [];

  const draftCount = items.filter((i) => i.status === 'draft').length;
  const approvedCount = items.filter((i) => i.status === 'approved').length;
  const submittedCount = items.filter(
    (i) => i.status === 'submitted' || i.status === 'under_review',
  ).length;
  const removedCount = items.filter((i) => i.status === 'removed').length;

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Rezensionsfälle</h1>
          <p className="muted">
            Prüfung und Bearbeitung potenziell policywidriger Google-Bewertungen.
          </p>
        </div>
        <div>
          <Button onClick={() => setShowCreate(true)}>+ Neuer Review-Fall</Button>
        </div>
      </header>

      {/* COMPLIANCE ALERT */}
      <div className="alert" data-tone="warning" role="alert">
        <strong>Rechtlicher &amp; Compliance-Hinweis:</strong>
        <p style={{ marginTop: '4px' }}>
          Keine Löschgarantie; nur policywidrige Inhalte dürfen gemeldet werden. MailFlow 2.0 führt
          niemals eine automatische Meldung durch, garantiert keine Entfernungen und fordert keine
          unwahren Angaben an. Jeder Fall erfordert die explizite Autorisierung und Freigabe durch
          den Kunden.
        </p>
      </div>

      {/* METRICS SUMMARY */}
      <div className="metrics-grid-4">
        <div className="card">
          <div className="card__body">
            <span className="muted text-sm">Entwürfe</span>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>{draftCount}</div>
          </div>
        </div>
        <div className="card">
          <div className="card__body">
            <span className="muted text-sm">Vom Kunden freigegeben</span>
            <div
              style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 700,
                color: 'var(--color-accent)',
              }}
            >
              {approvedCount}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card__body">
            <span className="muted text-sm">Eingereicht / In Prüfung</span>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>{submittedCount}</div>
          </div>
        </div>
        <div className="card">
          <div className="card__body">
            <span className="muted text-sm">Erfolgreich entfernt</span>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--color-success)' }}>
              {removedCount}
            </div>
          </div>
        </div>
      </div>

      {/* CASE LIST TABLE */}
      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Fallübersicht</h2>
          {isLoading ? (
            <p className="muted">Fälle werden geladen …</p>
          ) : items.length === 0 ? (
            <p className="muted">Noch keine Review-Policy-Fälle angelegt.</p>
          ) : (
            // Sieben Spalten passen auf kein Telefon: der Kasten scrollt, nicht die Seite.
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Unternehmensprofil</th>
                    <th>Autor / Sterne</th>
                    <th>Kategorie</th>
                    <th>Status</th>
                    <th>Kundenautorisiert</th>
                    <th>Erstellt am</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.businessProfileName}</strong>
                        {item.companyName && (
                          <div className="text-sm muted">{item.companyName}</div>
                        )}
                      </td>
                      <td>
                        {item.reviewAuthor ?? 'Anonym'}
                        {item.reviewRating && (
                          <span className="badge" style={{ marginLeft: '8px' }}>
                            ★ {item.reviewRating}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge">{formatCategory(item.policyCategory)}</span>
                      </td>
                      <td>
                        <span className="badge" data-status={item.status}>
                          {formatStatus(item.status)}
                        </span>
                      </td>
                      <td>
                        {item.clientAuthorizationConfirmed ? (
                          <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>
                            ✓ Bestätigt
                          </span>
                        ) : (
                          <span className="muted">Ausstehend</span>
                        )}
                      </td>
                      <td className="text-sm muted">
                        {new Date(item.createdAt).toLocaleDateString('de-CH')}
                      </td>
                      <td>
                        <Button variant="ghost" onClick={() => setSelectedCaseId(item.id)}>
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {showCreate && (
        <CreateCaseModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['review-cases'] });
          }}
        />
      )}

      {selectedCaseId && (
        <CaseDetailModal
          caseId={selectedCaseId}
          onClose={() => setSelectedCaseId(null)}
          onUpdated={() => {
            void queryClient.invalidateQueries({ queryKey: ['review-cases'] });
          }}
        />
      )}
    </div>
  );
}

function formatCategory(cat: string): string {
  const map: Record<string, string> = {
    conflict_of_interest: 'Interessenkonflikt',
    fake_content: 'Falsche/Erfundene Inhalte',
    harassment: 'Belästigung/Mobbing',
    hate_speech: 'Hassrede',
    profanity: 'Beleidigung/Obszönität',
    off_topic: 'Themenfremd/Off-Topic',
    personal_info: 'Datenschutzverletzung',
    other: 'Sonstige Policyverletzung',
  };
  return map[cat] ?? cat;
}

function formatStatus(status: string): string {
  const map: Record<string, string> = {
    draft: 'Entwurf',
    pending_client_approval: 'Wartet auf Kundenfreigabe',
    approved: 'Vom Kunden freigegeben',
    submitted: 'Bei Google eingereicht',
    under_review: 'In Prüfung',
    removed: 'Bewertung entfernt',
    rejected: 'Abgelehnt',
    appealed: 'Einspruch eingereicht',
    closed: 'Geschlossen',
  };
  return map[status] ?? status;
}

function CreateCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [businessProfileName, setBusinessProfileName] = useState('');
  const [reviewAuthor, setReviewAuthor] = useState('');
  const [reviewRating, setReviewRating] = useState('1');
  const [reviewText, setReviewText] = useState('');
  const [policyCategory, setPolicyCategory] = useState('off_topic');
  const [evidenceDetails, setEvidenceDetails] = useState('');
  const [clientAuth, setClientAuth] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/review-cases', {
        businessProfileName,
        reviewAuthor: reviewAuthor || null,
        reviewRating: Number(reviewRating),
        reviewText: reviewText || null,
        policyCategory,
        evidenceDetails: evidenceDetails || null,
        clientAuthorizationConfirmed: clientAuth,
      }),
    onSuccess: () => {
      onCreated();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientAuth) return;
    create.mutate();
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" style={{ maxWidth: '600px' }}>
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Neuen Review-Policy-Fall anlegen</h2>

          {create.isError && (
            <div className="alert" data-tone="danger" role="alert">
              {create.error instanceof Error ? create.error.message : 'Fehler beim Anlegen.'}
            </div>
          )}

          <form onSubmit={handleSubmit} className="stack" style={{ gap: 'var(--space-4)' }}>
            <div className="field">
              <label className="field__label">Unternehmensprofil (Google Business Profile) *</label>
              <input
                className="input"
                value={businessProfileName}
                onChange={(e) => setBusinessProfileName(e.target.value)}
                placeholder="z. B. Mustermann Kanzlei Zürich"
                required
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label className="field__label">Autor der Bewertung</label>
                <input
                  className="input"
                  value={reviewAuthor}
                  onChange={(e) => setReviewAuthor(e.target.value)}
                  placeholder="z. B. Max M."
                />
              </div>
              <div className="field">
                <label className="field__label">Sterne-Bewertung</label>
                <select
                  className="input"
                  value={reviewRating}
                  onChange={(e) => setReviewRating(e.target.value)}
                >
                  <option value="1">1 Stern ★</option>
                  <option value="2">2 Sterne ★★</option>
                  <option value="3">3 Sterne ★★★</option>
                  <option value="4">4 Sterne ★★★★</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label className="field__label">Policy-Kategorie der Verletzung *</label>
              <select
                className="input"
                value={policyCategory}
                onChange={(e) => setPolicyCategory(e.target.value)}
              >
                <option value="conflict_of_interest">
                  Interessenkonflikt (z. B. Ex-Mitarbeiter / Konkurrent)
                </option>
                <option value="fake_content">Erfundene Inhalte / Kein Kundenkontakt</option>
                <option value="harassment">Belästigung / Mobbing</option>
                <option value="hate_speech">Hassrede / Diskriminierung</option>
                <option value="profanity">Beleidigung / Obszönität</option>
                <option value="off_topic">Themenfremd (Falsches Unternehmen)</option>
                <option value="personal_info">Datenschutzverletzung (Name/Daten Dritter)</option>
                <option value="other">Sonstige Google-Richtlinienverletzung</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label">Bewertungstext</label>
              <textarea
                className="input"
                rows={3}
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Exakter Wortlaut der Bewertung …"
              />
            </div>

            <div className="field">
              <label className="field__label">Begründung &amp; Evidenz (Sachlicher Nachweis)</label>
              <textarea
                className="input"
                rows={3}
                value={evidenceDetails}
                onChange={(e) => setEvidenceDetails(e.target.value)}
                placeholder="Warum verletzt diese Bewertung die Google-Richtlinien? (z. B. Person war nie Mandant) …"
              />
            </div>

            {/* MANDATORY CLIENT AUTHORIZATION CHECK */}
            <div
              style={{
                padding: 'var(--space-3)',
                backgroundColor: 'var(--color-surface-raised)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
              }}
            >
              <label className="check" style={{ fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={clientAuth}
                  onChange={(e) => setClientAuth(e.target.checked)}
                  required
                />
                Ich bestätige, dass der Kunde uns schriftlich mit der Prüfung dieses Falles
                beauftragt hat.
              </label>
              <p className="muted text-sm" style={{ marginTop: '4px' }}>
                Pflichtbestätigung: Es werden keine Meldungen ohne expliziten Kundenauftrag
                durchgeführt.
              </p>
            </div>

            <div className="dialog__actions">
              <Button variant="ghost" onClick={onClose} type="button">
                Abbrechen
              </Button>
              <Button type="submit" disabled={!clientAuth} loading={create.isPending}>
                Fall anlegen
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

interface ReviewCaseDetail extends ReviewCaseItem {
  reviewText: string | null;
  evidenceDetails: string | null;
  notes: string | null;
  appealSubmittedAt: string | null;
}

function CaseDetailModal({
  caseId,
  onClose,
  onUpdated,
}: {
  caseId: string;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['review-case', caseId],
    queryFn: () => api.get<{ case: ReviewCaseDetail }>(`/api/review-cases/${caseId}`),
  });
  const [approvalProof, setApprovalProof] = useState('');
  const [showApprovalInput, setShowApprovalInput] = useState(false);
  const [appealNotes, setAppealNotes] = useState('');
  const [showAppealInput, setShowAppealInput] = useState(false);

  const approve = useMutation({
    mutationFn: () => api.post(`/api/review-cases/${caseId}/approve`, { proof: approvalProof }),
    onSuccess: () => {
      setShowApprovalInput(false);
      onUpdated();
    },
  });

  const appeal = useMutation({
    mutationFn: () => api.post(`/api/review-cases/${caseId}/appeal`, { appealNotes }),
    onSuccess: () => {
      setShowAppealInput(false);
      onUpdated();
    },
  });

  const item = data?.case;

  if (isLoading) {
    return (
      <div className="overlay">
        <div className="card dialog">
          <div className="card__body">
            <p className="muted">Wird geladen …</p>
          </div>
        </div>
      </div>
    );
  }

  if (!item) return null;

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" style={{ maxWidth: '650px' }}>
        <div className="card__body stack">
          <header
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div>
              <h2 style={{ fontSize: 'var(--text-xl)', margin: 0 }}>{item.businessProfileName}</h2>
              <span className="badge" data-status={item.status} style={{ marginTop: '4px' }}>
                {formatStatus(item.status)}
              </span>
            </div>
            <Button variant="ghost" onClick={onClose}>
              Schließen
            </Button>
          </header>

          <div className="detail-grid">
            <dl className="definition-grid">
              <div>
                <dt>Autor / Sterne</dt>
                <dd>
                  {item.reviewAuthor ?? 'Anonym'} (★ {item.reviewRating ?? 1})
                </dd>
              </div>
              <div>
                <dt>Policy-Kategorie</dt>
                <dd>{formatCategory(item.policyCategory)}</dd>
              </div>
              <div>
                <dt>Kundenautorisiert</dt>
                <dd>{item.clientAuthorizationConfirmed ? '✓ Ja' : 'Nein'}</dd>
              </div>
              <div>
                <dt>Kundenfreigabe Datum</dt>
                <dd>
                  {item.clientApprovedAt
                    ? new Date(item.clientApprovedAt).toLocaleString('de-CH')
                    : 'Ausstehend'}
                </dd>
              </div>
            </dl>
          </div>

          {item.reviewText && (
            <div>
              <strong>Bewertungstext:</strong>
              <p
                className="card"
                style={{ padding: 'var(--space-3)', fontStyle: 'italic', marginTop: '4px' }}
              >
                „{item.reviewText}“
              </p>
            </div>
          )}

          {item.evidenceDetails && (
            <div>
              <strong>Evidenz &amp; Begründung:</strong>
              <p className="muted text-sm" style={{ marginTop: '4px' }}>
                {item.evidenceDetails}
              </p>
            </div>
          )}

          {item.status === 'draft' && (
            <div
              className="card"
              style={{ padding: 'var(--space-4)', backgroundColor: 'var(--color-surface-raised)' }}
            >
              <h4 style={{ margin: 0 }}>Kundenfreigabe erfassen</h4>
              <p className="text-sm muted">
                Vor der Einreichung bei Google muss die schriftliche Freigabe des Kunden
                dokumentiert werden.
              </p>
              {showApprovalInput ? (
                <div
                  className="stack"
                  style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
                >
                  <input
                    className="input"
                    placeholder="Nachweis der Freigabe (z. B. E-Mail Bestätigung vom 01.08.2026)"
                    value={approvalProof}
                    onChange={(e) => setApprovalProof(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      onClick={() => approve.mutate()}
                      disabled={!approvalProof.trim()}
                      loading={approve.isPending}
                    >
                      Freigabe speichern
                    </Button>
                    <Button variant="ghost" onClick={() => setShowApprovalInput(false)}>
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  style={{ marginTop: 'var(--space-2)' }}
                  onClick={() => setShowApprovalInput(true)}
                >
                  + Kundenfreigabe dokumentieren
                </Button>
              )}
            </div>
          )}

          {item.status === 'rejected' && !item.appealSubmittedAt && (
            <div className="card" style={{ padding: 'var(--space-4)', backgroundColor: 'var(--color-warning-subtle)' }}>
              <h4 style={{ margin: 0 }}>Einmaliger Einspruch (Appeal)</h4>
              <p className="text-sm muted">
                Falls Google die erste Meldung abgelehnt hat, kann einmalig ein detaillierter
                Einspruch eingereicht werden.
              </p>
              {showAppealInput ? (
                <div
                  className="stack"
                  style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
                >
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Zusätzliche Nachweise & Begründung für den Einspruch …"
                    value={appealNotes}
                    onChange={(e) => setAppealNotes(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      onClick={() => appeal.mutate()}
                      disabled={!appealNotes.trim()}
                      loading={appeal.isPending}
                    >
                      Einspruch einreichen
                    </Button>
                    <Button variant="ghost" onClick={() => setShowAppealInput(false)}>
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  style={{ marginTop: 'var(--space-2)' }}
                  onClick={() => setShowAppealInput(true)}
                >
                  Einspruch vorbereiten
                </Button>
              )}
            </div>
          )}

          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              borderTop: '1px solid var(--color-border)',
              paddingTop: '8px',
            }}
          >
            Hinweis: MailFlow 2.0 garantiert keine Löschung. Nur Richtlinienverstöße werden
            bearbeitet.
          </div>
        </div>
      </div>
    </div>
  );
}
